/**
 * Zahlungsdialoge V2: Fokusführung und Tastensperre (nur Darstellung/Bedienung).
 *
 * - Fokusfalle: Tab und Umschalt+Tab bleiben im Dialog.
 * - Tastensperre: Während kritischer Abläufe fängt der Dialog Esc/F2/F3 in der Erfassungsphase
 *   am Fenster ab, bevor die Tastenkürzel des Verkaufs sie sehen (die hören in der
 *   Bubbling-Phase am Fenster). So schließt Esc den Bar-Dialog nicht während der Verarbeitung,
 *   und F3 startet bei offenem Bar-Dialog keine Karte daneben. Die Kürzel selbst bleiben
 *   unverändert.
 */
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';

const FOKUSSIERBAR = [
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function fokussierbare(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOKUSSIERBAR)].filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/** Tab am Rand des Dialogs springt zum anderen Ende (Fokusfalle). */
export function fokusFalleTaste(
  e: ReactKeyboardEvent<HTMLElement> | KeyboardEvent,
  container: HTMLElement,
): void {
  if (e.key !== 'Tab') {
    return;
  }
  const liste = fokussierbare(container);
  const erstes = liste[0];
  const letztes = liste[liste.length - 1];
  if (erstes === undefined || letztes === undefined) {
    e.preventDefault();
    container.focus();
    return;
  }
  const aktiv = document.activeElement;
  if (e.shiftKey && (aktiv === erstes || aktiv === container || !container.contains(aktiv))) {
    e.preventDefault();
    letztes.focus();
  } else if (!e.shiftKey && (aktiv === letztes || !container.contains(aktiv))) {
    e.preventDefault();
    erstes.focus();
  }
}

/**
 * Beim Schließen: Fokus zurück an das Element vor dem Dialog – nur wenn er sonst verloren
 * wäre (auf `body`), damit ein danach geöffneter Dialog (z. B. Beleg) seinen Fokus behält.
 */
export function useFokusRueckgabe(): void {
  useEffect(() => {
    const vorher = document.activeElement;
    return () => {
      const jetzt = document.activeElement;
      if (
        vorher instanceof HTMLElement &&
        vorher !== document.body &&
        vorher.isConnected &&
        (jetzt === null || jetzt === document.body)
      ) {
        vorher.focus();
      }
    };
  }, []);
}

/**
 * Codex-Prüfung ZD-V2 (M4): Fokuswächter – gerät der Fokus programmatisch oder per Klick hinter
 * den Dialog (z. B. `focus()` eines Elements im Verkauf), holt der Dialog ihn zurück: an das
 * zuletzt fokussierte Element im Dialog, sonst an `ersatz` bzw. den Dialog selbst. Der Wächter
 * wird beim Schließen vollständig entfernt.
 */
export function useFokusWaechter(
  dialog: { readonly current: HTMLElement | null },
  ersatz: () => HTMLElement | null,
): void {
  const ersatzRef = useRef(ersatz);
  ersatzRef.current = ersatz;
  useEffect(() => {
    let zuletzt: HTMLElement | null = null;
    const beiFokus = (e: FocusEvent): void => {
      const d = dialog.current;
      const ziel = e.target;
      if (d === null || !d.isConnected || !(ziel instanceof Node)) {
        return;
      }
      if (d.contains(ziel)) {
        zuletzt = ziel instanceof HTMLElement ? ziel : null;
        return;
      }
      // Fokus in einem anderen modalen Dialog (z. B. Bezahlentscheidung): nicht zurückholen
      if (ziel instanceof Element && ziel.closest('[aria-modal="true"]') !== null) {
        return;
      }
      const zurueck =
        zuletzt !== null && zuletzt.isConnected && d.contains(zuletzt) && !isDisabled(zuletzt)
          ? zuletzt
          : (ersatzRef.current() ?? d);
      zurueck.focus();
    };
    document.addEventListener('focusin', beiFokus);
    return () => {
      document.removeEventListener('focusin', beiFokus);
    };
  }, [dialog]);
}

function isDisabled(el: HTMLElement): boolean {
  return (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) && el.disabled;
}

/**
 * Fängt die Tasten `gesperrt` am Fenster ab (Erfassungsphase) und meldet sie über
 * `onGesperrt`. Leere Liste = keine Sperre.
 */
export function useTastensperre(
  gesperrt: readonly string[],
  onGesperrt: (taste: string) => void,
): void {
  const melden = useRef(onGesperrt);
  melden.current = onGesperrt;
  const schluessel = gesperrt.join('|');
  useEffect(() => {
    if (schluessel === '') {
      return;
    }
    const tasten = new Set(schluessel.split('|'));
    const abfangen = (e: KeyboardEvent): void => {
      if (!tasten.has(e.key)) {
        return;
      }
      e.preventDefault();
      // stopPropagation: die Kürzel des Verkaufs (Bubbling am Fenster) sehen die Taste nicht;
      // stopImmediatePropagation: auch später am Fenster angemeldete Erfassungs-Listener nicht.
      // Früher angemeldete Erfassungs-Listener (Scanner-Erkennung des Verkaufs) laufen davor
      // und bleiben unverändert (Reihenfolge per Test abgesichert).
      e.stopPropagation();
      e.stopImmediatePropagation();
      melden.current(e.key);
    };
    window.addEventListener('keydown', abfangen, true);
    return () => {
      window.removeEventListener('keydown', abfangen, true);
    };
  }, [schluessel]);
}

/** Lesbarer Name einer Taste für den Sperrhinweis. */
export function tastenName(taste: string): string {
  return taste === 'Escape' ? 'Esc' : taste;
}

/**
 * ZD-V2-F4: Fokusfalle für die bisherigen Zahlungsdialoge (Schalter AUS), die Trinkgeldfrage und
 * die Bezahlentscheidung. Am Fenster in der Erfassungsphase, damit Tab auch dann im Dialog
 * bleibt, wenn der Fokus noch hinter der Abdunkelung liegt – sonst erreichte Tab die Reiter der
 * Kopfzeile, und Enter wechselte während einer Bar- oder Kartenzahlung den Reiter. Beim Öffnen
 * wandert ein Fokus von außerhalb an den Dialog selbst (braucht `tabIndex={-1}`), nie an eine
 * Taste wie „Zahlung abbrechen“; gerät der Fokus später hinter den Dialog (Klick, `focus()`),
 * holt ihn der Fokuswächter zurück (Codex-Prüfung A2, Runde 1). Die V2-Dialoge haben ihre eigene
 * Fokusfalle (`aktiv` = false, ihr Element ist dann nicht gerendert). Keine Tastensperre, keine
 * Optik.
 */
export function useFokusfalleAmFenster(
  dialog: { readonly current: HTMLElement | null },
  aktiv: boolean,
): void {
  // Der Wächter wirkt nur, solange das Element des Dialogs gerendert ist (sonst ohne Wirkung).
  useFokusWaechter(dialog, () => null);
  useEffect(() => {
    if (!aktiv) {
      return;
    }
    const d = dialog.current;
    if (d !== null && !d.contains(document.activeElement)) {
      d.focus();
    }
    const abfangen = (e: KeyboardEvent): void => {
      const c = dialog.current;
      if (c?.isConnected === true) {
        fokusFalleTaste(e, c);
      }
    };
    window.addEventListener('keydown', abfangen, true);
    return () => {
      window.removeEventListener('keydown', abfangen, true);
    };
  }, [dialog, aktiv]);
}
